import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    TagIcon,
    CurrencyRupeeIcon,
    PhotoIcon,
    CubeIcon,
    StarIcon,
    XMarkIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminProducts = () => {
    // New Arrivals Management
    const [newArrivals, setNewArrivals] = useState([]);

    useEffect(() => {
        fetchNewArrivals();
    }, []);

    const fetchNewArrivals = async () => {
        try {
            const response = await axios.get(`${API}/products?is_new_arrival=true`);
            setNewArrivals(response.data.map(p => p.id));
        } catch (error) {
            setNewArrivals([]);
        }
    };

    const handleAddNewArrival = async (productId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API}/products/${productId}/new-arrival`,
                { is_new_arrival: true },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchNewArrivals();
            toast.success('Product added to New Arrivals!');
        } catch (error) {
            toast.error('Failed to add product to New Arrivals');
        }
    };

    const handleRemoveNewArrival = async (productId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API}/products/${productId}/new-arrival`,
                { is_new_arrival: false },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchNewArrivals();
            toast.success('Product removed from New Arrivals!');
        } catch (error) {
            toast.error('Failed to remove product from New Arrivals');
        }
    };

    const [products, setProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 10;
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [filterCategory, setFilterCategory] = useState('');
    const [filterSubcategory, setFilterSubcategory] = useState('');

    // Update subcategories when filterCategory changes
    useEffect(() => {
        if (filterCategory) {
            fetchSubcategories(filterCategory);
        }
        setFilterSubcategory('');
    }, [filterCategory]);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        subcategory_id: '',
        price: '',
        sizes: [],
        is_customizable: false,
        quantity: '',
        product_details: '',
        bullet_points_title: '',
        bullet_points: ''
    });

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchSizes();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await axios.get(`${API}/subcategories?category_id=${categoryId}`);
            setSubcategories(response.data);
        } catch (error) {
            console.error('Failed to fetch subcategories:', error);
        }
    };

    const fetchSizes = async () => {
        try {
            const response = await axios.get(`${API}/sizes`);
            setSizes(response.data);
        } catch (error) {
            console.error('Failed to fetch sizes:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            // Upload images first if any are selected
            let uploadedImageUrls = [];
            if (imageFiles.length > 0) {
                uploadedImageUrls = await uploadImages();
            }

            const productData = {
                ...formData,
                price: parseFloat(formData.price),
                quantity: parseInt(formData.quantity),
                images: uploadedImageUrls.length > 0 ? uploadedImageUrls : (editingProduct ? editingProduct.images : [])
            };

            if (editingProduct) {
                await axios.put(`${API}/products/${editingProduct.id}`, productData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Product updated successfully!');
            } else {
                await axios.post(`${API}/products`, productData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Product added successfully!');
            }

            // Reset form
            setFormData({
                title: '',
                description: '',
                category_id: '',
                subcategory_id: '',
                price: '',
                sizes: [],
                is_customizable: false,
                quantity: '',
                product_details: '',
                bullet_points_title: '',
                bullet_points: ''
            });
            setImageFiles([]);
            setShowAddForm(false);
            setEditingProduct(null);
            fetchProducts();
        } catch (error) {
            toast.error('Failed to save product');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/products/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Product deleted successfully!');
            fetchProducts();
        } catch (error) {
            toast.error('Failed to delete product');
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            title: product.title,
            description: product.description,
            category_id: product.category_id,
            subcategory_id: product.subcategory_id || '',
            price: product.price.toString(),
            sizes: product.sizes,
            is_customizable: product.is_customizable,
            quantity: product.quantity.toString(),
            product_details: product.product_details || '',
            bullet_points_title: product.bullet_points_title || '',
            bullet_points: product.bullet_points || ''
        });
        setShowAddForm(true);
        if (product.category_id) {
            fetchSubcategories(product.category_id);
        }
    };

    const handleCategoryChange = (categoryId) => {
        setFormData({ ...formData, category_id: categoryId, subcategory_id: '' });
        if (categoryId) {
            fetchSubcategories(categoryId);
        }
    };

    const handleSizeToggle = (sizeName) => {
        const newSizes = formData.sizes.includes(sizeName)
            ? formData.sizes.filter(s => s !== sizeName)
            : [...formData.sizes, sizeName];
        setFormData({ ...formData, sizes: newSizes });
    };

    const handleImageUpload = (files) => {
        const newFiles = Array.from(files).map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        setImageFiles(prev => [...prev, ...newFiles]);
    };

    const removeImage = (index) => {
        setImageFiles(prev => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const uploadImages = async () => {
        if (imageFiles.length === 0) return [];

        setUploadingImages(true);
        const uploadedUrls = [];

        try {
            for (const imageFile of imageFiles) {
                const formData = new FormData();
                formData.append('file', imageFile.file);
                formData.append('folder', 'products');

                const token = localStorage.getItem('token');
                const response = await axios.post(`${API}/upload-image`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                uploadedUrls.push(response.data.image_url);
            }
            return uploadedUrls;
        } catch (error) {
            console.error('Image upload failed:', error);
            toast.error('Failed to upload images');
            return [];
        } finally {
            setUploadingImages(false);
        }
    };

    // Pagination logic
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    // Filter products by category and subcategory
    const filteredProducts = products.filter(product => {
        let match = true;
        if (filterCategory) match = match && product.category_id === filterCategory;
        if (filterSubcategory) match = match && product.subcategory_id === filterSubcategory;
        return match;
    });
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(products.length / productsPerPage);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Products Management</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage your inventory, prices, and stock</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddForm(true)}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm hover:shadow-md flex items-center font-medium text-sm"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Product
                </motion.button>
            </div>

            {/* Category/Subcategory Filters */}
            <div className="flex flex-wrap gap-4 mb-8 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="flex items-center text-gray-500 mr-2">
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    <span className="font-medium text-sm">Filters:</span>
                </div>
                <div className="flex-1 min-w-[200px] relative">
                    <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <select
                        value={filterCategory}
                        onChange={e => setFilterCategory(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-gray-700 appearance-none text-sm"
                    >
                        <option value="">All Categories</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px] relative">
                    <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <select
                        value={filterSubcategory}
                        onChange={e => setFilterSubcategory(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-gray-700 appearance-none text-sm"
                        disabled={!filterCategory}
                    >
                        <option value="">All Subcategories</option>
                        {subcategories.map(subcategory => (
                            <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Add/Edit Product Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 mb-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                    {editingProduct ? <PencilSquareIcon className="h-5 w-5 mr-2 text-gray-700" /> : <PlusIcon className="h-5 w-5 mr-2 text-gray-700" />}
                                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                                </h3>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Left Column: Details */}
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Product Title</label>
                                            <div className="relative">
                                                <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                <input
                                                    type="text"
                                                    required
                                                    value={formData.title}
                                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="Enter product title"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Price (₹)</label>
                                                <div className="relative">
                                                    <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                    <input
                                                        type="number"
                                                        required
                                                        min="0"
                                                        step="0.01"
                                                        value={formData.price}
                                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Stock</label>
                                                <div className="relative">
                                                    <CubeIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                    <input
                                                        type="number"
                                                        required
                                                        min="0"
                                                        value={formData.quantity}
                                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Category</label>
                                                <div className="relative">
                                                    <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                    <select
                                                        required
                                                        value={formData.category_id}
                                                        onChange={(e) => handleCategoryChange(e.target.value)}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none text-sm"
                                                    >
                                                        <option value="">Select Category</option>
                                                        {categories.map((category) => (
                                                            <option key={category.id} value={category.id}>
                                                                {category.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Subcategory</label>
                                                <div className="relative">
                                                    <TagIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                                    <select
                                                        value={formData.subcategory_id}
                                                        onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value })}
                                                        className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none text-sm"
                                                        disabled={!filterCategory && !formData.category_id}
                                                    >
                                                        <option value="">Select Subcategory</option>
                                                        {subcategories
                                                            .filter(sub => sub.category_id === formData.category_id)
                                                            .map((subcategory) => (
                                                                <option key={subcategory.id} value={subcategory.id}>
                                                                    {subcategory.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Sizes</label>
                                            <div className="flex flex-wrap gap-2">
                                                {sizes.map((size) => (
                                                    <label key={size.id} className="inline-flex items-center cursor-pointer group">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.sizes.includes(size.name)}
                                                                onChange={() => handleSizeToggle(size.name)}
                                                                className="peer sr-only"
                                                            />
                                                            <div className="w-8 h-8 rounded-lg border border-gray-200 peer-checked:border-gray-900 peer-checked:bg-gray-900 peer-checked:text-white flex items-center justify-center transition-all">
                                                                <span className="text-xs font-medium text-gray-600 peer-checked:text-white">{size.name}</span>
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Options</label>
                                            <label className="inline-flex items-center cursor-pointer p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors w-full">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.is_customizable}
                                                    onChange={e => setFormData({ ...formData, is_customizable: e.target.checked })}
                                                    className="form-checkbox h-4 w-4 text-gray-900 rounded border-gray-300 focus:ring-gray-900"
                                                />
                                                <span className="ml-3 text-gray-700 font-medium text-sm">Is Customizable?</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Right Column: Description & Images */}
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                                            <textarea
                                                required
                                                rows="6"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="Product description..."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Product Details</label>
                                            <textarea
                                                rows="4"
                                                value={formData.product_details}
                                                onChange={(e) => setFormData({ ...formData, product_details: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                placeholder="Detailed product information..."
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Bullet Points Title</label>
                                                <input
                                                    type="text"
                                                    value={formData.bullet_points_title}
                                                    onChange={(e) => setFormData({ ...formData, bullet_points_title: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="e.g., Key Features"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Bullet Points (One per line)</label>
                                                <textarea
                                                    rows="4"
                                                    value={formData.bullet_points}
                                                    onChange={(e) => setFormData({ ...formData, bullet_points: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                    placeholder="• Feature 1&#10;• Feature 2&#10;• Feature 3"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Product Images</label>
                                            <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors bg-white group cursor-pointer relative h-[200px] flex flex-col justify-center items-center">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={e => handleImageUpload(e.target.files)}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                />
                                                <div className="flex flex-col items-center">
                                                    <div className="p-3 bg-gray-50 rounded-full mb-2 group-hover:bg-gray-100 transition-colors">
                                                        <PhotoIcon className="h-6 w-6 text-gray-500" />
                                                    </div>
                                                    <span className="text-gray-700 font-medium text-sm">Click to upload images</span>
                                                    <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-4 mt-4">
                                                {imageFiles.map((img, idx) => (
                                                    <motion.div
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        key={idx}
                                                        className="relative group"
                                                    >
                                                        <img src={img.preview} alt={`Preview ${idx}`} className="h-20 w-20 object-cover rounded-lg shadow-sm border border-gray-100" />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(idx)}
                                                            className="absolute -top-2 -right-2 bg-white text-gray-500 border border-gray-200 rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-all hover:text-red-500"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-6 border-t border-gray-200">
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-1 text-sm"
                                    >
                                        {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="button"
                                        onClick={() => { setShowAddForm(false); setEditingProduct(null); setFormData({ title: '', description: '', category_id: '', subcategory_id: '', price: '', sizes: [], is_customizable: false, quantity: '', product_details: '', bullet_points_title: '', bullet_points: '' }); }}
                                        className="bg-white text-gray-700 border border-gray-300 px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm"
                                    >
                                        Cancel
                                    </motion.button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Products List */}
            <div className="overflow-x-auto custom-scrollbar rounded-2xl border border-gray-100 bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50/50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Price</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Stock</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">New Arrival</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        <AnimatePresence>
                            {currentProducts.map((product, index) => (
                                <motion.tr
                                    key={product.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="hover:bg-gray-50/80 transition-colors group"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 mr-4 border border-gray-100">
                                                <img
                                                    src={product.images[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab'}
                                                    alt={product.title}
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{product.title}</div>
                                                <div className="text-xs text-gray-500 max-w-[200px] truncate">{product.description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₹{product.price}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <CubeIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                            {product.quantity}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${product.is_customizable
                                            ? 'bg-green-50 text-green-700 border border-green-100'
                                            : 'bg-gray-50 text-gray-600 border border-gray-100'
                                            }`}>
                                            {product.is_customizable ? 'Customizable' : 'Standard'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <button
                                            onClick={async () => {
                                                if (newArrivals.includes(product.id)) {
                                                    await handleRemoveNewArrival(product.id);
                                                } else {
                                                    await handleAddNewArrival(product.id);
                                                }
                                            }}
                                            className={`p-1.5 rounded-full transition-colors ${newArrivals.includes(product.id)
                                                ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                                                : 'text-gray-300 hover:text-yellow-500 hover:bg-gray-100'
                                                }`}
                                            title="Toggle New Arrival"
                                        >
                                            <StarIcon className={`h-4 w-4 ${newArrivals.includes(product.id) ? 'fill-current' : ''}`} />
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            {product.is_customizable && (
                                                <motion.button
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    onClick={() => window.location.href = `/admin/products/${product.id}/template`}
                                                    className="p-1.5 text-purple-400 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors"
                                                    title="Edit Template"
                                                >
                                                    <PhotoIcon className="h-4 w-4" />
                                                </motion.button>
                                            )}
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => handleEdit(product)}
                                                className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <PencilSquareIcon className="h-4 w-4" />
                                            </motion.button>
                                            <motion.button
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                onClick={() => handleDelete(product.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </motion.button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))}
                        </AnimatePresence>
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-6 px-4">
                <div className="text-xs text-gray-500">
                    Showing <span className="font-medium text-gray-900">{indexOfFirstProduct + 1}</span> to <span className="font-medium text-gray-900">{Math.min(indexOfLastProduct, products.length)}</span> of <span className="font-medium text-gray-900">{products.length}</span> results
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <div className="flex items-center space-x-1">
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`w-7 h-7 rounded-md text-xs font-medium transition-colors ${currentPage === i + 1
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default AdminProducts;
